import {
	AxiosInstance,
	AxiosRequestConfig,
	isAxiosError,
	isCancel,
} from 'axios';
import { ApiResponse } from '../types';
import intercept from './interceptor';

type KeyValue<U> = {
	[key: string]: U;
};
//USING AXIOS INSTANCE INSTEAD DEFAULT
const axiosDefaultInstance: AxiosInstance = intercept(
	import.meta.env.VITE_BASE_URL || 'http://localhost:3000',
);
export const axiosChatInstance: AxiosInstance = intercept(
	import.meta.env.VITE_CHAT_BASE_URL || 'http://localhost:3000',
);

const axiosIdentityInstance: AxiosInstance = intercept(
	import.meta.env.VITE_IDENTITY_BASE_URL,
);

const axiosCruzControlInstance: AxiosInstance = intercept(
	import.meta.env.VITE_CTRL_BASE_URL,
);

const axiosVendorsInstance: AxiosInstance = intercept(
	import.meta.env.VITE_VENDORS_BASE_URL,
);

export const axiosAiInstance: AxiosInstance = intercept(
	import.meta.env.VITE_AI_BASE_URL,
);

const handleError = (err: unknown) => {
	if (isAxiosError(err) && err.response?.data) {
		return { data: err.response.data };
	}
	return {
		data: {
			success: false,
			errors: [
				{
					key: 'Request error',
					message: 'Request error',
				},
			],
		},
	};
};

class Api<T> {
	constructor(private axiosInstance: AxiosInstance) {}

	async get(
		url: string,
		params: unknown,
		headers: KeyValue<string> = {},
		config: AxiosRequestConfig = {},
	) {
		const response = await this.axiosInstance
			.get(url, {
				params,
				headers: {
					...headers,
				},
				...config,
				paramsSerializer: {
					indexes: null,
				},
			})
			.catch(err => {
				if (config.signal && isCancel(err)) {
					throw err;
				}
				return handleError(err);
			});
		return response.data;
	}

	async post(
		url: string,
		body: T,
		config: AxiosRequestConfig = {},
		// Existing callers depend on this untyped response contract across the app.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	): Promise<ApiResponse<any>> {
		// console.log(body)
		const response = await this.axiosInstance
			.post(url, body, config)
			.catch(err => handleError(err));
		//console.log(response)
		return response.data;
	}

	async postStream(
		url: string,
		body: T,
		config: AxiosRequestConfig = {},
	): Promise<ReadableStream<Uint8Array>> {
		const response = await this.axiosInstance.post(url, body, {
			...config,
			adapter: 'fetch',
			responseType: 'stream',
		});

		if (!(response.data instanceof ReadableStream)) {
			throw new Error(
				'Streaming responses are not supported by this browser.',
			);
		}

		return response.data;
	}

	async put(url: string, body: T, headers: KeyValue<string> = {}) {
		const response = await this.axiosInstance
			.put(url, body, {
				headers: {
					...headers,
				},
			})
			.catch(err => handleError(err));
		return response.data;
	}

	async patch(url: string, body: T, headers: KeyValue<string> = {}) {
		const response = await this.axiosInstance
			.patch(url, body, {
				headers: {
					...headers,
				},
			})
			.catch(err => handleError(err));
		return response.data;
	}

	async delete(url: string, body?: T, headers: KeyValue<string> = {}) {
		const response = await this.axiosInstance
			.delete(url, {
				data: body,
				headers,
			})
			.catch(err => handleError(err));
		return response.data;
	}
}

// api instance for chat
export const API_CHAT = new Api(axiosChatInstance);
// api instance for identity
export const API_IDENTITY = new Api(axiosIdentityInstance);
// api instance for cruz control
export const API_CTRL = new Api(axiosCruzControlInstance);
// api instance for vendors
export const API_VENDOR = new Api(axiosVendorsInstance);
// api instance for AI
export const API_AI = new Api(axiosAiInstance);

export default new Api(axiosDefaultInstance);
